import { Box, Button, Chip, Stack, SvgIcon, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import AgenticEmptyState from 'components/@home/@settings/Tabs/Agents/AgenticEmptyState';
import ManageAgentKnowledgeDialog from 'components/@home/@settings/Tabs/Agents/ManageAgentKnowledgeDialog';
import AgenticPanelToolbar from 'components/@home/@settings/Tabs/Agents/panels/AgenticPanelToolbar';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import DatabaseOutlineIcon from 'mdi-react/DatabaseOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import { arrayOf, object } from 'prop-types';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { attachedSkillsForAgent, catalogKnowledgeRows } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';

const nameCellSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '2px',
  height: '100%',
  minWidth: 0,
  pt: 1,
  pb: 1,
};

const nameRowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  fontWeight: 600,
  color: 'text.primary',
  lineHeight: 1.3,
};

const descriptionSx = {
  fontSize: theme => theme.typography.pxToRem(12),
  color: 'text.secondary',
  lineHeight: 1.3,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  pl: 3,
};

const KnowledgePanel = ({ agent, knowledge = [], skills = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { flow, authCompany, onCreateKnowledge } = useAgentEdit();
  const [manageOpen, setManageOpen] = React.useState(false);

  const skillChipSx = useMemo(() => {
    const mutedChip = semanticTone(theme, 'default');
    return {
      height: 22,
      fontSize: theme.typography.pxToRem(11),
      backgroundColor: mutedChip.bg,
      color: mutedChip.fg,
      '& .MuiChip-label': { padding: '0 8px' },
    };
  }, [theme]);

  const attachedSkills = useMemo(() => attachedSkillsForAgent(agent, skills), [agent, skills]);

  const rows = useMemo(() => catalogKnowledgeRows(knowledge, skills), [knowledge, skills]);

  const handleOpenRow = row => {
    if (!flow?._id || !row?._id) return;
    navigate(
      buildAgenticPath(authCompany, {
        flowId: flow._id,
        section: 'knowledge',
        knowledgeId: String(row._id),
      }),
    );
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: I18n.t('AgenticChatFlow.Name'),
        flex: 2,
        minWidth: 220,
        valueGetter: (value, row) => row.displayName || row.name,
        renderCell: ({ row }) => (
          <Box sx={nameCellSx}>
            <Box component="span" sx={nameRowSx}>
              <DatabaseOutlineIcon size={16} />
              {row.displayName || row.name}
            </Box>
            {row.description ? (
              <Box sx={descriptionSx} title={row.description}>
                {row.description}
              </Box>
            ) : null}
          </Box>
        ),
      },
      {
        field: 'skills',
        headerName: I18n.t('AgenticChatFlow.Skills'),
        flex: 1.2,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => {
          const list = row.skillsForKnowledge || [];
          if (list.length === 0) return '—';
          return (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                flexWrap: 'wrap',
              }}
            >
              {list.map(s => (
                <Chip key={s._id} size="small" label={s.displayName || s.name} sx={skillChipSx} />
              ))}
            </Stack>
          );
        },
      },
    ],
    [skillChipSx],
  );

  const toolbarActions = (
    <>
      <Button variant="outlined" color="secondary" onClick={() => setManageOpen(true)}>
        <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
          <CogOutlineIcon />
        </SvgIcon>
        {I18n.t('AgenticChatFlow.ManageKnowledge')}
      </Button>
      {onCreateKnowledge && (
        <Button variant="outlined" color="secondary" onClick={onCreateKnowledge}>
          <SvgIcon sx={{ fontSize: 18, mr: '5px', color: 'primary.main' }}>
            <PlusIcon />
          </SvgIcon>
          {I18n.t('AgenticChatFlow.CreateKnowledge')}
        </Button>
      )}
    </>
  );

  return (
    <Box
      sx={{
        p: 3,
        px: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        flex: 1,
        minHeight: 0,
      }}
    >
      {rows.length === 0 ? (
        <AgenticEmptyState
          concept="tool"
          title={I18n.t('AgenticChatFlow.EmptyKnowledge.Title')}
          lead={I18n.t('AgenticChatFlow.EmptyKnowledge.Lead')}
          secondaryLabel={I18n.t('AgenticChatFlow.ManageKnowledge')}
          secondaryIcon={<CogOutlineIcon size={16} />}
          onSecondary={() => setManageOpen(true)}
          primaryLabel={onCreateKnowledge ? I18n.t('AgenticChatFlow.CreateKnowledge') : null}
          primaryIcon={<PlusIcon size={16} />}
          onPrimary={onCreateKnowledge || undefined}
        />
      ) : (
        <>
          <Typography
            sx={{
              fontSize: theme.typography.pxToRem(22),
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {I18n.t('AgenticChatFlow.AllKnowledge')}
          </Typography>
          <OmmnioDataGrid
            rows={rows}
            columns={columns}
            columnHeaderHeight={48}
            getRowId={r => r._id}
            autoHeight={false}
            disableEvenOdd
            rowHeight={64}
            defaultSortModel={[{ field: 'name', sort: 'asc' }]}
            onCellClick={cell => handleOpenRow(cell.row)}
            slotProps={{
              toolbar: {
                searchPlaceholder: I18n.t('AgenticChatFlow.Search'),
                children: toolbarActions,
              },
            }}
            slots={{ toolbar: AgenticPanelToolbar }}
            sx={{
              flex: 1,
              minHeight: 320,
              borderRadius: 1,
              '& .MuiDataGrid-cell': { cursor: 'pointer' },
            }}
          />
        </>
      )}

      <ManageAgentKnowledgeDialog
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        knowledge={knowledge}
        attachedSkills={attachedSkills}
        onCreateKnowledge={() => {
          setManageOpen(false);
          onCreateKnowledge?.();
        }}
      />
    </Box>
  );
};

KnowledgePanel.propTypes = {
  agent: object.isRequired,
  knowledge: arrayOf(object),
  skills: arrayOf(object),
};

export default KnowledgePanel;
